/**
 * 会员 / 牛币付费弹窗「宿主代理」桥接模块。
 *
 * 背景：AI 进宝以 iframe 形式嵌入宿主（当前仅路演 portal）。当内资研报 / 外资研报 / 点评在无权限时
 * 需要「开通会员」或「牛币充值」，iframe 视口受限、体验差。该模块将这两类付费请求通过 postMessage
 * 发送给宿主，由宿主用自身的会员 / 牛币能力处理，处理完成后回包，iframe 据此重查权限并刷新。
 *
 * 协议（详见 docs/design-member-nbi-delegate-to-host.md）：
 * - iframe → 宿主：{ source:'jinbao-ai-chat', type:'member-pay-required'|'nbi-recharge-required', reqId, payload }
 * - 宿主 → iframe：{ source:'jinbao-host', type:'member-pay-result'|'nbi-recharge-result', reqId, status }
 *   status: 'pending' | 'success' | 'cancelled' | 'closed' | 'unsupported'
 *
 * 超时与 ack：发送后启动一个「接管超时」，仅用于探测宿主是否会接管本次请求。
 * 宿主收到并打开弹窗后须立即回 status='pending' 的 ack，本端据此取消超时并无限等待最终结果；
 * 若超时仍未收到任何回包，则视为宿主不支持/未监听，reject 让调用方回退本地弹窗。
 *
 * 安全：接收端校验 event.origin（若已配置 hostOrigin）与 event.data.source === 'jinbao-host'；
 * 发送端 target origin 使用配置的 hostOrigin，未配置时退化为 '*'（仅建议在受控环境）。
 */

/** 本端（iframe）消息来源标识 */
const SELF_SOURCE = 'jinbao-ai-chat';
/** 宿主消息来源标识 */
const HOST_SOURCE = 'jinbao-host';
/** 等待宿主回包的默认超时时间（毫秒），超时按「宿主未响应」回退本地弹窗 */
const DEFAULT_TIMEOUT = 8000;
/** 生成随机串时的进制（36 进制：0-9 + a-z），用于 reqId 兜底 */
const RANDOM_RADIX = 36;

/** 模块级配置：是否启用代理、宿主 origin */
const bridgeConfig = {
  enabled: false,
  hostOrigin: ''
};

/** 待回包的请求映射：reqId -> { resolve, reject, timer } */
const pendingMap = new Map();

/** 是否已安装全局 message 监听，避免重复绑定 */
let listenerInstalled = false;

/**
 * 生成请求关联用的唯一 reqId。
 * @returns {string} 唯一标识
 */
const genReqId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(RANDOM_RADIX).slice(2)}`;
};

/**
 * 处理来自宿主的回包消息，按 reqId 兑现对应的 Promise。
 * @param {MessageEvent} event - window message 事件
 * @returns {void}
 */
const handleHostMessage = (event) => {
  // 已配置宿主 origin 时做严格 origin 校验，未配置则跳过（受控环境兜底）
  if (bridgeConfig.hostOrigin && event.origin !== bridgeConfig.hostOrigin) {
    return;
  }
  const data = event.data;
  // 仅处理宿主来源、且带 reqId 的回包，避免误处理无关 postMessage
  if (!data || data.source !== HOST_SOURCE || !data.reqId) {
    return;
  }
  const pending = pendingMap.get(data.reqId);
  if (!pending) {
    // 找不到对应请求：可能已超时被清理，丢弃过期回包
    return;
  }
  // ack：宿主已接管（弹窗已打开），取消超时并继续等待最终结果（用户支付耗时不可控）
  if (data.status === 'pending') {
    clearTimeout(pending.timer);
    pending.timer = null;
    return;
  }
  clearTimeout(pending.timer);
  pendingMap.delete(data.reqId);
  pending.resolve(data.status || 'closed');
};

/**
 * 确保已安装全局 message 监听（懒安装，仅安装一次）。
 * @returns {void}
 */
const ensureListener = () => {
  if (listenerInstalled) {
    return;
  }
  window.addEventListener('message', handleHostMessage);
  listenerInstalled = true;
};

/**
 * 配置桥接模块。由宿主壳（chatExternalIframe）在确定代理开关后调用。
 * @param {{ enabled?: boolean, hostOrigin?: string }} options - 配置项
 * @returns {void}
 */
export const configureMemberBridge = (options = {}) => {
  if (typeof options.enabled === 'boolean') {
    bridgeConfig.enabled = options.enabled;
  }
  if (typeof options.hostOrigin === 'string') {
    bridgeConfig.hostOrigin = options.hostOrigin;
  }
  if (bridgeConfig.enabled) {
    ensureListener();
  }
};

/**
 * 当前是否启用「宿主代理」。
 * @returns {boolean} 启用返回 true
 */
export const isMemberDelegateEnabled = () => bridgeConfig.enabled;

/**
 * 向宿主发送一个付费请求并等待回包。
 * @param {'member-pay-required'|'nbi-recharge-required'} type - 请求类型
 * @param {object} payload - 业务载荷（scene/goodsDesc/needNbi/balance/businessId/businessType 等）
 * @param {number} [timeout] - 超时毫秒数
 * @returns {Promise<'success'|'cancelled'|'closed'|'unsupported'>} 宿主处理结果状态；超时则 reject
 */
const sendRequest = (type, payload, timeout = DEFAULT_TIMEOUT) => {
  ensureListener();
  const reqId = genReqId();
  const message = {
    source: SELF_SOURCE,
    type,
    reqId,
    payload: payload || {}
  };

  return new Promise((resolve, reject) => {
    // 接管超时：宿主在此时间内未回 ack（pending）则视为不支持，reject 让调用方回退本地弹窗。
    // 收到 ack 后该 timer 会被清除，之后无限等待用户支付的最终结果。
    const timer = setTimeout(() => {
      pendingMap.delete(reqId);
      reject(new Error('member-bridge timeout'));
    }, timeout);

    pendingMap.set(reqId, { resolve, reject, timer });

    const target = bridgeConfig.hostOrigin || '*';
    window.parent.postMessage(message, target);
  });
};

/**
 * 请求宿主处理「开通会员」。
 * @param {object} payload - 载荷，建议含 { scene, goodsDesc, businessId, businessType }
 * @returns {Promise<'success'|'cancelled'|'closed'|'unsupported'>} 处理结果状态
 */
export const requestMemberPay = (payload) => sendRequest('member-pay-required', payload);

/**
 * 请求宿主处理「牛币充值」。
 * @param {object} payload - 载荷，建议含 { scene, needNbi, balance, businessId, businessType }
 * @returns {Promise<'success'|'cancelled'|'closed'|'unsupported'>} 处理结果状态
 */
export const requestNbiRecharge = (payload) => sendRequest('nbi-recharge-required', payload);
